import { useState } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';

// project-imports
import { PopupTransition } from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';
import { useSelector } from 'store';

// assets
import { Logout } from 'iconsax-react';

// ==============================|| DRAWER CONTENT - LOGOUT ||============================== //
//
// Quick logout action pinned to the bottom of the sidebar. Mirrors the NavItem
// look (icon + label, icon-only when the drawer is collapsed) but always asks
// for confirmation before signing the user out, since a stray click here is
// disruptive.

const NavLogout = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { drawerOpen } = useSelector((state) => state.menu);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    try {
      setBusy(true);
      await logout();
      navigate('/login', { state: { from: '' } });
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  // Simple, plain footer button — neutral sidebar text colour, subtle hover.
  const button = (
    <Button
      onClick={() => setOpen(true)}
      fullWidth
      disableElevation
      startIcon={drawerOpen ? <Logout size={18} /> : null}
      sx={{
        py: 1.5,
        borderRadius: 0,
        fontWeight: 400,
        color: 'secondary.main',
        bgcolor: 'transparent',
        justifyContent: 'center',
        '& .MuiButton-startIcon': { mr: drawerOpen ? 1 : 0 },
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {drawerOpen ? 'Logout' : <Logout size={22} />}
    </Button>
  );

  return (
    <>
      <Box sx={{ flexShrink: 0 }}>
        {drawerOpen ? (
          button
        ) : (
          <Tooltip title="Logout" placement="right">
            {button}
          </Tooltip>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => !busy && setOpen(false)}
        keepMounted
        TransitionComponent={PopupTransition}
        maxWidth="xs"
        aria-labelledby="logout-dialog-title"
      >
        <DialogTitle id="logout-dialog-title">Log out?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You will be signed out of Hexa Store and need to log in again to
            continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="secondary"
            onClick={() => setOpen(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={busy}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NavLogout;
